import type { SocialPlatform } from '@/types/connections'
import type { OpenScreenId, ScreenId } from '@/generated/screens.g'

export type ThreadType = 'chat'

export type AgentThreadStatus = 'active' | 'closed'

export type ServerMessageRole = 'user' | 'model' | 'system'

export type MessageRole = 'user' | 'assistant' | 'system'

export type TranscriptMessageRole = Exclude<MessageRole, 'system'>

export function toMessageRole(serverRole: ServerMessageRole): MessageRole {
  return serverRole === 'model' ? 'assistant' : serverRole
}

export interface ChatThread {
  readonly id: string
  readonly type: ThreadType
  readonly status: AgentThreadStatus
  readonly title: string
  readonly messageCount: number
  readonly lastMessageAt: string | null
  readonly createdAt: string | null
}

/** One image on a user message; `src` is a local preview for the optimistic bubble. */
export interface ChatMessageAttachment {
  readonly itemId?: string
  readonly src?: string
  readonly alt?: string
}

/** The kinds of item a message can be about; each maps to the nav row the item lives in. */
export type MessageContextItemKind = 'post' | 'document' | 'insight' | 'design'

/** The one item the user pointed at when sending; display-only on the message. */
export interface MessageContextItem {
  readonly kind: MessageContextItemKind
  readonly ref: string
  readonly excerpt?: string
}

/** What the composer chips showed when a message was sent: the screen name and the attached item. */
export interface MessageContext {
  readonly screen?: string
  readonly item?: MessageContextItem
}

export interface ChatMessage {
  readonly id: string
  readonly threadId: string
  readonly role: MessageRole
  readonly text: string
  readonly index: number
  readonly createdAt: string | null
  readonly steps?: readonly AgentMessageStep[]
  readonly attachments?: readonly ChatMessageAttachment[]
  /** What the composer chips showed when the user sent this message; absent on older messages. */
  readonly context?: MessageContext
}

export const PENDING_MESSAGE_ID_PREFIX = 'local-'

export function isPendingMessage(message: Pick<ChatMessage, 'id'>): boolean {
  return message.id.startsWith(PENDING_MESSAGE_ID_PREFIX)
}

export interface SendMessageInput {
  readonly threadId?: string
  readonly threadType?: ThreadType
  readonly message: string
  readonly confirmActionId?: string
  readonly declineActionId?: string
  readonly answerQuestionId?: string
  readonly answerValue?: string
  readonly viewContext?: ViewContext
  /** The chips the composer showed, stored on the saved message for display; never sent with an action or question answer. */
  readonly messageContext?: MessageContext
  /** Images the user attached to this turn, already uploaded to the library; never sent with an action or question answer. */
  readonly attachments?: readonly { itemId: string }[]
}

export const MAX_MESSAGE_ATTACHMENTS = 4

/** The closed vocabulary of the DIRECTED layer; `selection` carries `excerpt`, `action` has nothing to quote, `design` carries the selected element's HTML as `excerpt`. */
type ViewContextAttachmentKind = 'selection' | 'action' | 'design'

export interface ViewContextAttachment {
  readonly kind: ViewContextAttachmentKind
  readonly ref: string
  readonly excerpt?: string
}

/** One small factual claim about the current screen's state, such as "status: draft". */
export interface ViewContextStateFact {
  readonly key: string
  readonly value: string
}

/** The record an item screen has open, as the agent sees it. */
interface ViewContextItem {
  readonly id: string
  readonly label: string
}

export interface ViewContext {
  readonly screen: ScreenId
  readonly item?: ViewContextItem
  readonly state?: readonly ViewContextStateFact[]
  readonly attachments?: readonly ViewContextAttachment[]
}

/** Why the client refused a stage move; kept locally, never reported to the agent. */
export type StageDeclineReason = 'no-screen' | 'blocked' | 'user-busy'

export interface SendMessageResult {
  readonly threadId: string
  readonly messageId: string
  readonly text: string
}

export interface DeleteThreadInput {
  readonly threadId: string
}

export interface DeleteThreadResult {
  readonly success: true
}

type AgentSourceKind = 'web' | 'knowledge'

export interface AgentSource {
  readonly kind: AgentSourceKind
  readonly ref: string
  readonly title?: string
  /** An https URL on our own storage; absent when the site has none. */
  readonly favicon?: string
}

export type ToolKind =
  | 'thinking'
  | 'web-search'
  | 'research'
  | 'drafting'
  | 'editing'
  | 'generating'
  | 'navigating'

/** Closed caption vocabulary a `tool_call` step's live label is drawn from; mirrors the server's `AGENT_LABEL_KEYS`. */
export type AgentLabelKey =
  | 'fallback.thinking'
  | 'fallback.webSearch'
  | 'fallback.research'
  | 'fallback.drafting'
  | 'fallback.editing'
  | 'fallback.generating'
  | 'fallback.navigating'
  | 'question.asking'
  | 'documents.listing'
  | 'documents.reading'
  | 'knowledge.listing'
  | 'knowledge.readingPage'
  | 'knowledge.organizing'
  | 'inbox.checking'
  | 'memoryCandidate.noting'
  | 'memory.checking'
  | 'memory.readingPage'
  | 'memory.followingLink'
  | 'socialConnect.offeringLinkedIn'
  | 'socialConnect.checkingLinkedIn'
  | 'research.checkingAnalytics'
  | 'research.checkingTrackedCompetitors'
  | 'research.checkingCompetitorReport'
  | 'research.checkingRecentContent'
  | 'research.readingPost'
  | 'research.searchingWeb'
  | 'research.checkingSimilarCompanies'
  | 'research.readingLinkedInPage'
  | 'research.checkingPostHistory'
  | 'research.readingPlan'
  | 'research.checkingDesigns'
  | 'research.readingPrompts'
  | 'research.readingResearch'
  | 'competitor.adding'
  | 'competitors.analyzing'
  | 'styleGuide.readingPosts'
  | 'visualStyle.checking'
  | 'visualStyle.writing'
  | 'onboarding.completing'
  | 'post.editing'
  | 'page.writing'
  | 'page.adding'
  | 'topic.adding'
  | 'post.writing'
  | 'note.logging'
  | 'post.publishingNow'
  | 'companyProfile.readingWebsite'
  | 'brandFoundation.drafting'
  | 'post.scheduling'
  | 'knowledgePage.writing'
  | 'document.writingNamed'
  | 'document.writingGeneric'
  | 'document.approvingNamed'
  | 'document.approvingGeneric'
  | 'stage.openingScreen'
  | 'stage.openingGeneric'
  | 'workspace.listingFiles'
  | 'workspace.readingFile'
  | 'workspace.writingFile'
  | 'workspace.editingFile'
  | 'workspace.deletingFile'
  | 'workspace.renderingPage'
  | 'workspace.listingReferences'
  | 'workspace.readingReference'
  | 'design.starting'
  | 'design.checkingRun'
  | 'design.attachingToPost'
  | 'design.addingReference'
  | 'design.checkingReferences'
  | 'design.editing'
  | 'design.checkingEdit'

/** Data a translated label or outcome string interpolates. */
export type AgentCopyParams = Record<string, string | number>

/** Closed outcome-row vocabulary for a `tool_result` row; mirrors the server's `AGENT_OUTCOME_KEYS`. */
type AgentOutcomeKey =
  | 'documentUpdated'
  | 'documentApproved'
  | 'styleGuideBuilt'
  | 'visualStyleSaved'
  | 'companyProfileSaved'
  | 'competitorAdded'
  | 'competitorsAnalyzed'
  | 'pageWriteStarted'
  /** `addPage`'s outcome: one page added to the plan by hand; `params` carries `title`. */
  | 'pageAdded'
  /** `addTopic`'s outcome: one topic added to the topics under review; `params` carries `title`. */
  | 'topicAdded'
  | 'postWriteStarted'
  | 'postScheduled'
  | 'postPublished'
  | 'postEdited'
  | 'noteLogged'
  | 'knowledgePageWritten'
  | 'knowledgeOrganized'
  /** `showOnStage`'s outcome: a link to the artifact it opened; `params` carries `screen`, optionally `itemId`. */
  | 'artifactLinked'
  /** `designPost`'s outcome: one design run started; `params` carries `designId`, `designName`. */
  | 'designRunStarted'
  /** `attachDesignToPost`'s outcome; `params` carries `designId`, `postId`. */
  | 'designAttachedToPost'
  /** `addDesignReference`'s outcome; `params` carries `referenceId`, `name`. */
  | 'designReferenceAdded'
  /** `editDesign`'s outcome: Raldo started changing a design; `params` carries `designId`, `runId`. */
  | 'designEditStarted'

export interface AgentOutcome {
  readonly key: AgentOutcomeKey
  readonly params?: AgentCopyParams
}

interface TextChunk {
  readonly type: 'text'
  readonly text: string
}

interface ToolCallChunk {
  readonly type: 'tool_call'
  readonly id: string
  readonly tool: string
  readonly kind: ToolKind
  readonly labelKey: AgentLabelKey
  readonly labelParams?: AgentCopyParams
}

interface ToolResultChunk {
  readonly type: 'tool_result'
  readonly id: string
  readonly ok: boolean
  readonly sources?: readonly AgentSource[]
  /** The tool's client-facing result row, present only when worth showing. */
  readonly outcome?: AgentOutcome
}

/** A streamed request to put a screen in front of the user; nothing has navigated when it arrives. */
export interface StageChunk {
  readonly type: 'stage'
  readonly id: string
  readonly screen: OpenScreenId
  readonly itemId?: string
}

export type AgentStreamChunk = TextChunk | ToolCallChunk | ToolResultChunk | StageChunk

export type PendingActionStatus = 'pending' | 'executed' | 'declined' | 'expired'

export interface PendingAction {
  readonly id: string
  readonly toolName: string
  readonly summary: string
  readonly input: Readonly<Record<string, unknown>>
  readonly status: PendingActionStatus
  readonly createdAt: string | null
  /** After this instant the server refuses the action. `null` when unstamped. */
  readonly expiresAt: string | null
  readonly failureReason?: string
  readonly undoable?: boolean
  readonly undoNote?: string
}

export type PendingQuestionStatus = 'pending' | 'answered' | 'expired'

export interface PendingQuestion {
  readonly id: string
  readonly question: string
  readonly options: readonly string[]
  readonly allowFreeText: boolean
  readonly status: PendingQuestionStatus
  readonly requestedByUserId: string
  readonly createdAt: string | null
  /** After this instant the server refuses the answer. `null` when unstamped. */
  readonly expiresAt: string | null
  readonly resolvedAt?: string | null
  readonly answer?: string
}

export type PendingConnectStatus = 'pending' | 'completed' | 'expired'

export interface PendingConnect {
  readonly id: string
  readonly platform: SocialPlatform
  readonly status: PendingConnectStatus
  readonly requestedByUserId: string
  readonly createdAt: string | null
  /** After this instant the server no longer honours the request. `null` when unstamped. */
  readonly expiresAt: string | null
  readonly resolvedAt?: string | null
}

export interface AgentMessageStep {
  readonly id: string
  readonly tool?: string
  readonly kind: ToolKind
  readonly labelKey: AgentLabelKey
  readonly labelParams?: AgentCopyParams
  readonly startedAt: string
  readonly completedAt?: string
  readonly ok: boolean
  readonly sources?: readonly AgentSource[]
  readonly outcome?: AgentOutcome
}
